import { formatDecimalHours } from '@coneno/c-sheet-shared'
import type { TaskSlice } from '@/lib/report-summary'
import { cn } from '@/lib/utils'
import { ChartTooltip } from './chart-tooltip'
import { useChartHover } from './use-chart-hover'

// One hue per project; a project's tasks are lighter shades of it.
const projectHues = [205, 265, 150, 45, 330, 100, 290, 15]
const maxSlices = 8

type TaskSplitChartProps = {
  slices: TaskSlice[]
  projectName(id: string): string
  taskName(id: string): string
  totalMinutes: number
  /** Called with the hovered slice's project and task, or null when none is hovered. */
  onHighlight?(slice: TaskHighlight | null): void
}

export type TaskHighlight = { projectId: string; taskId: string | null }

type Segment = {
  key: string
  highlight: TaskHighlight | null
  label: string
  minutes: number
  color: string
}

function segments(
  slices: TaskSlice[],
  projectName: (id: string) => string,
  taskName: (id: string) => string
): Segment[] {
  const projectOrder = [...new Set(slices.map((slice) => slice.projectId))]
  const shown = slices.slice(0, slices.length > maxSlices ? maxSlices - 1 : maxSlices)
  const taskIndex = new Map<string, number>()
  const result: Segment[] = shown.map((slice) => {
    const hue = projectHues[projectOrder.indexOf(slice.projectId) % projectHues.length]
    const index = taskIndex.get(slice.projectId) ?? 0
    taskIndex.set(slice.projectId, index + 1)
    return {
      key: `${slice.projectId}:${slice.taskId ?? ''}`,
      highlight: { projectId: slice.projectId, taskId: slice.taskId },
      label: `${projectName(slice.projectId)} · ${slice.taskId ? taskName(slice.taskId) : 'No task'}`,
      minutes: slice.minutes,
      color: `oklch(${Math.min(0.55 + index * 0.1, 0.85)} 0.12 ${hue})`
    }
  })
  const rest = slices.slice(shown.length).reduce((sum, slice) => sum + slice.minutes, 0)
  if (rest) {
    result.push({
      key: 'other',
      highlight: null,
      label: 'Other',
      minutes: rest,
      color: 'var(--muted-foreground)'
    })
  }
  return result
}

/** Donut of the report's hours by project and task, with the total in its centre. */
export function TaskSplitChart({
  slices,
  projectName,
  taskName,
  totalMinutes,
  onHighlight
}: TaskSplitChartProps): React.JSX.Element {
  const parts = segments(slices, projectName, taskName)
  const radius = 52
  const circumference = 2 * Math.PI * radius
  const gap = parts.length > 1 ? 1.5 : 0
  const total = `${formatDecimalHours(totalMinutes)} h`
  const share = (minutes: number): string =>
    `${formatDecimalHours(minutes)} h (${Math.round((minutes / totalMinutes) * 100)}%)`
  const { hover, track, clear } = useChartHover()
  let offset = 0

  return (
    <figure
      data-chart=""
      className="relative m-0 size-36 shrink-0"
      aria-label="Hours by project and task"
      onMouseLeave={() => {
        clear()
        onHighlight?.(null)
      }}
    >
      <svg viewBox="0 0 128 128" className="size-full -rotate-90" aria-hidden="true">
        <circle cx="64" cy="64" r={radius} fill="none" strokeWidth="16" className="stroke-muted" />
        {totalMinutes > 0 &&
          parts.map((part) => {
            const length = (part.minutes / totalMinutes) * circumference
            const dash = Math.max(length - gap, 0.5)
            const circle = (
              <circle
                key={part.key}
                cx="64"
                cy="64"
                r={radius}
                fill="none"
                strokeWidth="16"
                stroke={part.color}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                className={cn(
                  'transition-opacity',
                  hover && hover.key !== part.key && 'opacity-40'
                )}
                onMouseMove={(event) => {
                  track(event, part.key, part.label, share(part.minutes))
                  onHighlight?.(part.highlight)
                }}
              />
            )
            offset += length
            return circle
          })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        {/* Shrinks for long totals such as 1234,5 h so they stay inside the ring. */}
        <span
          className={cn(
            'font-semibold tabular-nums',
            total.length <= 6 ? 'text-xl' : total.length <= 8 ? 'text-lg' : 'text-base'
          )}
        >
          {total}
        </span>
      </div>
      <figcaption className="sr-only">
        <ul>
          {parts.map((part) => (
            <li key={part.key}>{`${part.label}: ${share(part.minutes)}`}</li>
          ))}
        </ul>
      </figcaption>
      <ChartTooltip hover={hover} />
    </figure>
  )
}
